import "./CaseReport.css";

import { format } from "date-fns";
import { DateTime } from "luxon";
import { ReactNode, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Case,
  CaseReportSignature,
  CliaCertificate,
  SlideClassification,
  Stain,
  User,
  UserCredentials,
  LocationCode,
} from "pwapi";

import { CaseAddon, CaseDiagnosis } from "custom-models";

import { fetchSignee, patchCaseAddon } from "shared/actions/caseDetailActions";
import { fetchDisclaimers } from "shared/actions/disclaimerActions";
import { fetchUserSignatures } from "shared/actions/sysActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SpecimenDetail } from "shared/reducers/specimenDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { specimen_source } from "shared/utils/specimenUtils";

import TextareaInput from "components/customInputs/TextareaInput";
import LogoLink from "components/header/LogoLink";
import {
  isDiagnosable,
  isDigitizationCode,
  isModifyingReport,
} from "components/viewer/dialogs/utils";
import PWLogo from "images/logo.png";
import PServicesLogo from "images/pathology_services-logo.svg";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

import CaseReportHeader from "./CaseReportHeader";
import SignatureField from "./SignatureField";
import {
  cptCodeStringFromDiagnoses,
  getLocationStringForSignature,
  getPhoneString,
  icd10StringFromDiagnoses,
  PW_DATETIME_WITH_ZONE,
} from "./utils";

type CaseReportProps = {
  caseDetailObject: CaseDetailState;
  currentUser?: User;
  actions?: ReactNode;
  diagnoses: DiagnosesObject[];
  selectedSignature?: CaseReportSignature;
  addon?: string;
  useChangeAddon?: boolean;
  isUnsigned?: boolean;
  isPreview?: boolean;
  features?: FeatureFlags;
  onChangeSignature?: Function;
  locationCode?: LocationCode;
};

type DiagnosesObject = {
  specimen: SpecimenDetail;
  diagnosis?: CaseDiagnosis;
  screenDiagnosis?: CaseDiagnosis;
};

const getTCLabInfo = (cert?: CliaCertificate) => {
  // Dropping back down to cert?.lab?.location for backwards compatibility
  // TODO: remove this drop back down after cert.location becomes required
  const location = cert?.location || cert?.lab?.location || undefined;

  return (
    cert && (
      <>
        {cert.name}
        <br />
        {location?.street}
        <br />
        {location?.street2 && (
          <>
            {location?.street2}
            <br />
          </>
        )}
        {location?.city}
        {", "}
        {location?.state} {location?.zipcode}
        <br />
        Phone: {getPhoneString(location?.phone1 || "")}
        <br />
        Fax: {getPhoneString(location?.fax || "")}
        <br />
        CLIA #: {cert.number}
        <br />
        Gross examination performed at: {cert.name}
      </>
    )
  );
};

const AddonItem = ({
  addon,
  orderNumber,
}: {
  addon: CaseAddon;
  orderNumber: number;
}) => {
  return (
    <>
      <tr>
        <td className="AddonItem__header">
          {addon.addon_type === "amend" ? "Amendment" : "Addendum"}{" "}
          {orderNumber}
        </td>
      </tr>
      <tr>
        <td>{addon.content}</td>
      </tr>
      <tr>
        <td className="AddonItem__user">
          {addon.user?.first_name}&nbsp;
          {addon.user?.last_name} -&nbsp;
          {addon.signed_at
            ? format(new Date(addon.signed_at), "MM/dd/yyyy hh:mm:ss a")
            : "Preview"}
        </td>
      </tr>
    </>
  );
};

const HiddenSignatureRows = ({
  onChangeSignature,
  user,
}: {
  onChangeSignature?: Function;
  user?: UserCredentials;
}) => (
  <>
    <tr className={onChangeSignature && "PrintReport__hiddenInfo"}>
      <td>
        {user?.first_name} {user?.last_name}
        {user?.userprofile?.credentials
          ? `, ${user.userprofile.credentials}`
          : ""}
      </td>
    </tr>
    {user?.userprofile?.title ? (
      <tr className={onChangeSignature && "PrintReport__hiddenInfo"}>
        <td>{user.userprofile.title}</td>
      </tr>
    ) : undefined}
  </>
);

const CaseReport = ({
  caseDetailObject,
  currentUser,
  actions,
  diagnoses,
  features,
  selectedSignature,
  onChangeSignature,
  useChangeAddon,
  isUnsigned,
  isPreview = true,
  locationCode,
}: CaseReportProps) => {
  const dispatch = useDispatch();
  const isModifyingRep =
    features && isModifyingReport(caseDetailObject, features, currentUser);
  const signatures = useSelector(
    ({ userSignatures }: ApplicationState) =>
      userSignatures?.[
        isModifyingRep?.source === CaseDiagnosis.source.CORRECTION
          ? caseDetailObject.report_signed_signature?.user_id || 0
          : currentUser?.id || 0
      ]
  );
  const defaultSignature =
    signatures?.find(
      (s) => s.id === caseDetailObject.finalizing_signature_id
    ) || signatures?.[0];

  const isLoadingSignatures = useIsLoading(fetchUserSignatures, {
    userId: caseDetailObject.report_signed_signature?.user_id || 0,
  });

  const isLoadingSignee = useIsLoading(fetchSignee, {
    id: caseDetailObject.report_signed_signature?.user_id || 0,
  });

  const disclaimers = useSelector(
    ({ disclaimers }: ApplicationState) => disclaimers
  );

  const stains = useAppSelector(({ stains }) => stains);

  useEffect(() => {
    if (onChangeSignature && !selectedSignature && !!signatures?.length) {
      onChangeSignature(
        isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
          caseDetailObject.report_signed_signature
          ? caseDetailObject.report_signed_signature
          : defaultSignature
      );
    }
  }, [
    onChangeSignature,
    selectedSignature,
    !!signatures?.length,
    defaultSignature,
  ]);

  // TODO: @James fix the deps in these `useEffect`s
  useEffect(() => {
    if (
      isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
      caseDetailObject.report_signed_signature?.user_id &&
      !signatures &&
      !isLoadingSignatures
    ) {
      dispatch(
        fetchUserSignatures({
          userId: caseDetailObject.report_signed_signature.user_id,
        })
      );
    }
  }, [caseDetailObject, currentUser]); // FIXME: Missing `isModifyingRep?.source`, etc

  useEffect(() => {
    if (
      isModifyingRep?.source === CaseDiagnosis.source.CORRECTION &&
      caseDetailObject.report_signed_signature?.user_id &&
      !caseDetailObject.report_signee &&
      !isLoadingSignee
    ) {
      dispatch(
        fetchSignee({ id: caseDetailObject.report_signed_signature.user_id })
      );
    }
  }, [caseDetailObject, currentUser]); // FIXME: Missing `isModifyingRep?.source`, etc

  const getAddonFromType = (type: string) =>
    caseDetailObject.case_addon
      ?.filter((f) => f.addon_type === type && f.signed_at)
      .sort((a, b) => ((b.signed_at || "") > (a.signed_at || "") ? 1 : -1));

  const affixMicroscopic = (
    diagnosis?: CaseDiagnosis,
    screenDiagnosis?: CaseDiagnosis
  ) => {
    if (
      diagnosis &&
      screenDiagnosis &&
      screenDiagnosis.user &&
      diagnosis.is_screen_valid
    ) {
      const screenDisclaimer = `Dr. ${screenDiagnosis.user.first_name} ${screenDiagnosis.user.last_name}, a board certified dermatopathologist, has reviewed the case and concurs.`;
      return diagnosis?.microscopic
        ? `${diagnosis.microscopic}\n${screenDisclaimer}`
        : screenDisclaimer;
    }
    return diagnosis?.microscopic;
  };

  const unsignedAddon = caseDetailObject.case_addon?.find(
    (f) => !f.signed_at && f.user?.id === currentUser?.id
  );

  const amendments = useMemo(
    () => getAddonFromType("amend"),
    [caseDetailObject]
  );

  const addenda = useMemo(() => getAddonFromType("addend"), [caseDetailObject]);

  const onChangeAddon = (e: any) => {
    unsignedAddon?.id &&
      dispatch(
        patchCaseAddon(
          unsignedAddon.id,
          unsignedAddon.addon_type,
          e.target.value
        )
      );
  };

  // TODO: Verify the back end currently includes these in its /api/cases/{id}/ response
  const anyClassifications = caseDetailObject.slides.some(
    (slide) =>
      slide.classifications?.filter(
        (s) => s.status === SlideClassification.status.SUCCEEDED
      ).length
  );

  const shouldShowMihmDisclaimer =
    (isDiagnosable(caseDetailObject) &&
      features?.prospectiveAI &&
      anyClassifications) ||
    // Viewing user does not have the flag, but user who finalized the case did-
    // so the case was mihm assisted and we should add the disclaimer
    (!isDiagnosable(caseDetailObject) &&
      diagnoses.some((diag) => diag?.diagnosis?.is_mihm_assisted));

  const shouldShowIHCDisclaimer = caseDetailObject.slides?.some(
    (s) =>
      stains[s.stain_id || 0]?.category === Stain.category.SPEC ||
      stains[s.stain_id || 0]?.category === Stain.category.IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.PW_SPEC ||
      stains[s.stain_id || 0]?.category === Stain.category.PW_IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.CED_IHC ||
      stains[s.stain_id || 0]?.category === Stain.category.CED_SPEC
  );

  const shouldShowDigitizationDisclaimer = diagnoses
    .flatMap((diag) => diag.diagnosis?.cpt_codes)
    .some((cpt) => cpt && isDigitizationCode(cpt));

  const dateReportedDiagnosis = diagnoses.find(
    (diag) => diag.diagnosis?.report_signed
  )?.diagnosis;

  useEffect(() => {
    // !disclaimer, if it already exists in state we don't need to fetch it again
    (shouldShowIHCDisclaimer ||
      shouldShowMihmDisclaimer ||
      shouldShowDigitizationDisclaimer) &&
      !disclaimers &&
      dispatch(fetchDisclaimers());
  }, [
    shouldShowIHCDisclaimer,
    shouldShowMihmDisclaimer,
    shouldShowDigitizationDisclaimer,
    disclaimers,
  ]);

  const branding = useMemo(() => {
    return caseDetailObject.grossed_by_cert?.name.includes("Pathology Services")
      ? {
          logo: PServicesLogo,
          alt: "Pathology Services",
          minHeight: "80px",
        }
      : {
          logo: PWLogo,
          alt: "Pathology Watch",
          minHeight: "80px",
        };
  }, [caseDetailObject]);

  const { REACT_APP_BACKEND_PROTOCOL, REACT_APP_BACKEND_HOST } = process.env;

  const urlPrefix = `${REACT_APP_BACKEND_PROTOCOL}://${
    REACT_APP_BACKEND_HOST
      ? REACT_APP_BACKEND_HOST.replace("api", "app")
      : "app.pathwatch.io"
  }/cases/`;

  return (
    <div className="PrintReportWrapper">
      <div className="ReportActionsBar ReportActionsHeader">
        <div className="ReportActionsBar__section ReportActionsBar--logo">
          <LogoLink goBack type="simple" />
          <span>Go back to {caseDetailObject.accession || "Case"}</span>
        </div>
      </div>
      <div className="ReportActionsBar ReportActionsFooter">
        <div className="ReportActionsBar__section">{actions}</div>
      </div>
      <div className="PrintReport">
        <table>
          <thead>
            <tr>
              <td>
                <div className="ReportHeader__logoSection">
                  <img
                    className="brand-img"
                    src={branding.logo}
                    alt={branding.alt}
                    height={branding.minHeight}
                  />
                  <h1>
                    {isPreview ? (
                      "PREVIEW REPORT"
                    ) : (
                      <>
                        DERMATOPATHOLOGY REPORT
                        <br />
                        <a href={`${urlPrefix}${caseDetailObject.accession}`}>
                          Secure Link to Digital Slide Display
                        </a>
                      </>
                    )}
                  </h1>
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <table>
                  <thead>
                    <tr>
                      <td>
                        <CaseReportHeader
                          caseDetailObject={caseDetailObject}
                          dateReported={
                            dateReportedDiagnosis?.report_signed
                              ? {
                                  timestamp:
                                    dateReportedDiagnosis?.report_signed,
                                  timezone:
                                    dateReportedDiagnosis?.report_signed_timezone,
                                }
                              : undefined
                          }
                          isUnsigned={isUnsigned}
                          selectedSignature={selectedSignature}
                        />
                      </td>
                    </tr>
                  </thead>
                  <tbody className="PrintReport__table--mainBody">
                    <tr>
                      <td>
                        {useChangeAddon && (
                          <div className="PrintReportSignature__selectBox">
                            <TextareaInput
                              label="Amendment or Addenda Reason"
                              value={unsignedAddon?.content}
                              onChange={onChangeAddon}
                            />
                          </div>
                        )}
                        {((amendments && amendments.length > 0) ||
                          (useChangeAddon &&
                            unsignedAddon?.addon_type === "amend")) && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              Amendments
                            </h2>
                            <table>
                              <tbody className="AddonItem">
                                {unsignedAddon?.addon_type === "amend" && (
                                  <AddonItem
                                    addon={unsignedAddon}
                                    orderNumber={(amendments?.length || 0) + 1}
                                  />
                                )}
                                {amendments &&
                                  amendments.map((amend, index) => {
                                    return (
                                      <AddonItem
                                        addon={amend}
                                        orderNumber={amendments.length - index}
                                        key={index}
                                      />
                                    );
                                  })}
                              </tbody>
                            </table>
                          </section>
                        )}
                        {((addenda && addenda.length > 0) ||
                          (useChangeAddon &&
                            unsignedAddon?.addon_type === "addend")) && (
                          <section className="PrintReportSection">
                            <h2 className="PrintReportSection__header">
                              Addenda
                            </h2>
                            <table>
                              <tbody className="AddonItem">
                                {unsignedAddon?.addon_type === "addend" && (
                                  <AddonItem
                                    addon={unsignedAddon}
                                    orderNumber={(addenda?.length || 0) + 1}
                                  />
                                )}
                                {addenda &&
                                  addenda.map((addend, index) => (
                                    <AddonItem
                                      addon={addend}
                                      orderNumber={addenda.length - index}
                                      key={index}
                                    />
                                  ))}
                              </tbody>
                            </table>
                          </section>
                        )}
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Final Diagnosis:
                          </h2>
                          <table className="PrintReportSection__table">
                            <tbody className="PrintReport__diagnosis">
                              {diagnoses.map((diag, index) => (
                                <tr key={index}>
                                  <td>{diag.specimen.name}.</td>
                                  <td>
                                    <span className="PrintReport__diagnosis--diagnosis">
                                      {specimen_source(diag.specimen)}
                                      :
                                      <br />
                                      {diag.diagnosis?.diagnosis}
                                    </span>
                                    {diag.diagnosis?.comment && (
                                      <>
                                        <br />
                                        <br />
                                        <span className="PrintReport__diagnosis--note">
                                          NOTE: {diag.diagnosis.comment}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </section>
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Clinical Information:
                          </h2>
                          <table className="PrintReportSection__table">
                            <tbody>
                              {diagnoses.map(
                                (diag, index) =>
                                  diag.specimen && (
                                    <tr key={index}>
                                      <td>{diag.specimen.name}.</td>
                                      {diag.specimen.clinical_history && (
                                        <td>
                                          {diag.specimen.clinical_history.toUpperCase()}
                                        </td>
                                      )}
                                    </tr>
                                  )
                              )}
                            </tbody>
                          </table>
                        </section>
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Microscopic Examination:
                          </h2>
                          <table className="PrintReportSection__table">
                            <tbody>
                              {diagnoses.map((diag, index) => (
                                <tr key={index}>
                                  <td>{diag.specimen.name}.</td>
                                  <td>
                                    {affixMicroscopic(
                                      diag.diagnosis,
                                      diag.screenDiagnosis
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {shouldShowMihmDisclaimer &&
                            disclaimers?.mihm_disclaimer && (
                              <span
                                key="mihmDisclaimer"
                                className="PrintReport__disclaimer"
                              >
                                {disclaimers.mihm_disclaimer}
                              </span>
                            )}
                          {shouldShowIHCDisclaimer &&
                            disclaimers?.ihc_disclaimer && (
                              <span
                                key="ihcDisclaimer"
                                className="PrintReport__disclaimer"
                              >
                                {disclaimers?.ihc_disclaimer}
                              </span>
                            )}
                          {shouldShowDigitizationDisclaimer &&
                            disclaimers?.digitization_disclaimer && (
                              <span
                                key="digitizationDisclaimer"
                                className="PrintReport__disclaimer"
                              >
                                {disclaimers?.digitization_disclaimer}
                              </span>
                            )}
                        </section>
                        <section className="PrintReportSection">
                          <h2 className="PrintReportSection__header">
                            Gross Description:
                          </h2>
                          <table className="PrintReportSection__table">
                            <tbody>
                              {diagnoses.map((diag, index) => (
                                <tr key={index}>
                                  <td>{diag.specimen.name}.</td>
                                  <td>{diag.specimen.gross_description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </section>
                        <section style={{ padding: "0.5em 0 1em 0" }}>
                          <h2 className="PrintReportSection__header">
                            FINALIZING PHYSICIAN
                          </h2>
                          <table
                            className="PrintReportSection__table"
                            style={{ width: "100%" }}
                          >
                            <tbody>
                              {caseDetailObject.source === Case.source.PW &&
                              !caseDetailObject.is_screening ? (
                                <>
                                  {onChangeSignature && signatures && (
                                    <tr>
                                      <td className="PrintReportSignature__selectBox">
                                        <SignatureField
                                          signatures={signatures}
                                          user={
                                            isModifyingRep?.source ===
                                              CaseDiagnosis.source.CORRECTION &&
                                            caseDetailObject.report_signee
                                              ? caseDetailObject.report_signee
                                              : currentUser
                                          }
                                          value={selectedSignature}
                                          onSelect={(newValue: any) => {
                                            onChangeSignature(newValue.label);
                                          }}
                                          locationCode={locationCode}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                  {selectedSignature && (
                                    <>
                                      <HiddenSignatureRows
                                        user={
                                          isModifyingRep?.source ===
                                          CaseDiagnosis.source.CORRECTION
                                            ? caseDetailObject.report_signee
                                            : caseDetailObject.active_assignment
                                        }
                                        onChangeSignature={onChangeSignature}
                                      />
                                      {dateReportedDiagnosis?.report_signed && (
                                        <tr
                                          className={
                                            onChangeSignature &&
                                            "PrintReport__hiddenInfo"
                                          }
                                        >
                                          <td>
                                            Final report electronically signed
                                            on{" "}
                                            {DateTime.fromISO(
                                              dateReportedDiagnosis.report_signed,
                                              {
                                                zone: dateReportedDiagnosis?.report_signed_timezone,
                                              }
                                            ).toLocaleString({
                                              ...PW_DATETIME_WITH_ZONE,
                                              timeZoneName: "long",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      <tr
                                        className={
                                          onChangeSignature &&
                                          "PrintReport__hiddenInfo"
                                        }
                                      >
                                        <td>
                                          The professional component was
                                          performed at:{" "}
                                          {
                                            selectedSignature.clia_certificate
                                              .name
                                          }
                                          ,{" "}
                                          {getLocationStringForSignature(
                                            selectedSignature
                                          )}
                                        </td>
                                      </tr>
                                      <tr
                                        className={
                                          onChangeSignature &&
                                          "PrintReport__hiddenInfo"
                                        }
                                      >
                                        <td>
                                          CLIA #:{" "}
                                          {
                                            selectedSignature.clia_certificate
                                              ?.number
                                          }
                                          {locationCode && (
                                            <> ; {locationCode.code}</>
                                          )}
                                        </td>
                                      </tr>
                                    </>
                                  )}
                                </>
                              ) : (
                                !caseDetailObject.is_screening && (
                                  <tr>
                                    <td>
                                      {
                                        caseDetailObject.active_assignment
                                          ?.first_name
                                      }{" "}
                                      {
                                        caseDetailObject.active_assignment
                                          ?.last_name
                                      }
                                    </td>
                                  </tr>
                                )
                              )}
                              {(isUnsigned ||
                                caseDetailObject.is_screening ||
                                caseDetailObject.status ===
                                  Case.status.LIS_PENDING) && (
                                <tr className="Preview__note">
                                  <td>
                                    {caseDetailObject.status ===
                                    Case.status.LIS_PENDING
                                      ? "Sent to LIS"
                                      : caseDetailObject.is_screening
                                      ? "Screening, Not Signing"
                                      : "Preview, Not Signed"}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </section>
                        <section>
                          <table className="PrintReportSection__table PrintReportSection__table--fixed PrintReportSection__table--preventBreak">
                            <tbody>
                              <tr>
                                {caseDetailObject.source === Case.source.PW &&
                                caseDetailObject.grossed_by_cert ? (
                                  <td>
                                    TC LABORATORY
                                    <br />
                                    {getTCLabInfo(
                                      caseDetailObject.grossed_by_cert
                                    )}
                                  </td>
                                ) : (
                                  <td>TC LABORATORY</td>
                                )}
                                <td data-testid="billingcodes">
                                  BILLING CODES:
                                  <br />
                                  ICD10:{" "}
                                  {icd10StringFromDiagnoses(
                                    diagnoses.reduce(
                                      (acc: CaseDiagnosis[], diag) => {
                                        return [
                                          ...acc,
                                          ...(diag.diagnosis
                                            ? [diag.diagnosis]
                                            : []),
                                        ];
                                      },
                                      []
                                    )
                                  )}
                                  <br />
                                  CPT:{" "}
                                  {cptCodeStringFromDiagnoses(
                                    diagnoses.reduce(
                                      (acc: CaseDiagnosis[], diag) => {
                                        return [
                                          ...acc,
                                          ...(diag.diagnosis
                                            ? [diag.diagnosis]
                                            : []),
                                        ];
                                      },
                                      []
                                    )
                                  )}
                                </td>
                                <td>
                                  EHR ROUTING IDENTIFIERS:
                                  <br />
                                  Encounter ID:{" "}
                                  {caseDetailObject.external_ezderm_order_id ||
                                    caseDetailObject.external_order_id}
                                  <br />
                                  Patient ID:{" "}
                                  {
                                    caseDetailObject.patient
                                      ?.medical_record_number
                                  }
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </section>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="PrintReport__hiddenInfo">
                    <tr>
                      <td>
                        <div style={{ height: "70px" }}>&nbsp;</div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </td>
            </tr>
          </tbody>
        </table>

        <footer className="PrintReport__footer">
          <span>Phone: (877) 463-5434 | Fax: (336) 360-0086</span>
          <span>
            PathologyWatch.com | Link to Digital Slide Display:{" "}
            <a href={`${urlPrefix}${caseDetailObject.accession}`}>
              {urlPrefix}
              {caseDetailObject.accession}
            </a>
          </span>
        </footer>
      </div>
    </div>
  );
};

export default CaseReport;
